import { useQuery } from '@tanstack/react-query';

import type { ResponseError } from 'Backend/common/types';
import type { operations, components } from 'Models/users.api.types';

import Config from 'Config';
import { getWithoutOverride } from 'Redux/api';
import { catchExpectedErrors } from '../common/catchExpectedErrors';
import { setAttachedCompanies } from './utils/attachedCompanies';

type ApiOperation = operations['getAttachedCompanies'];
export type ResponseStatusCode = keyof ApiOperation['responses'];
type ResponseBody<T extends ResponseStatusCode> = ApiOperation['responses'][T]['content']['application/json'];
type Query = ApiOperation['parameters']['query'];

const supportedStatusCodes = [200, 400, 403, 500] as const;

const isSupportedStatusCode = (status: number): status is (typeof supportedStatusCodes)[number] =>
    supportedStatusCodes.some((expected) => status === expected);

export type ResponseSuccess = ResponseBody<200>;
export type AttachedCompany = components['schemas']['APIUserCompany'];

const getAttachedCompanies = async (userStatus?: Query['status']): Promise<ResponseSuccess> => {
    const url = new URL(`${Config.users_v1_api}/companies`);

    if (userStatus) {
        url.searchParams.append('status', userStatus);
    }
    const result = await catchExpectedErrors(() => getWithoutOverride(url.toString(), {}, null, true, true));
    const status = result?.status;
    const data = result?.data;

    /* eslint-disable no-throw-literal -- Want to throw the response object directly and pass it on to react query */

    if (!isSupportedStatusCode(status)) {
        throw {
            title: 'Unknown error',
            detail: 'Something went wrong getting companies',
        } satisfies ResponseError;
    }
    switch (status) {
        case 200:
            !userStatus && setAttachedCompanies((data as ResponseBody<typeof status>).data ?? []);
            return data as ResponseBody<typeof status> satisfies ResponseSuccess;
        case 400:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        case 403:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        case 500:
            throw data as ResponseBody<typeof status> satisfies ResponseError;
        default:
            // Default is unreachable. Typescript gives an error if it is not.
            throw status satisfies never;
    }

    /* eslint-enable no-throw-literal -- Want to throw the response object directly and pass it on to react query */
};

export const useGetAttachedCompanies = ({ status }: { status?: Query['status'] } = {}) =>
    useQuery<ResponseSuccess, ResponseError>({
        queryKey: ['getAttachedCompanies', status],
        queryFn: () => getAttachedCompanies(status),
        enabled: true,
        refetchOnWindowFocus: false,
    });
